import { createContext, type ReactNode, useContext, useEffect, useRef } from "react";
import type { Me } from "../shared/types";
import { ApiError, SESSION_EXPIRED_EVENT, type SessionExpiredDetail } from "./lib/api";
import { useApi } from "./lib/useApi";

interface SessionValue {
  me: Me | undefined;
  loading: boolean;
  signedOut: boolean;
  /** True when showing the last known session because the phone has no signal. */
  offline: boolean;
  error: ApiError | null;
  reload: () => Promise<void>;
}

const SessionContext = createContext<SessionValue | null>(null);
const CACHE_KEY = "me:v1";

function readCache(): Me | undefined {
  try {
    const raw = localStorage.getItem(CACHE_KEY);
    return raw ? (JSON.parse(raw) as Me) : undefined;
  } catch {
    return undefined;
  }
}

function writeCache(me: Me | null) {
  try {
    if (me) localStorage.setItem(CACHE_KEY, JSON.stringify(me));
    else localStorage.removeItem(CACHE_KEY);
  } catch {}
}

export function SessionProvider({ children }: { children: ReactNode }) {
  const { data, loading, error, reload } = useApi<Me>("/me");
  const signedOut = error instanceof ApiError && (error.status === 401 || error.status === 403);
  // With no signal, keep the cleaner screens usable from the last known session so a
  // punch can still be captured and queued (it is sent and checked when back online).
  const networkDown = error instanceof ApiError && error.status === 0;
  const cached = networkDown && !data ? readCache() : undefined;

  useEffect(() => {
    if (data) writeCache(data);
    if (signedOut) writeCache(null);
  }, [data, signedOut]);

  useEffect(() => {
    if (!networkDown) return;
    const retry = () => void reload();
    window.addEventListener("online", retry);
    return () => window.removeEventListener("online", retry);
  }, [networkDown, reload]);

  // A page's request found the session gone (expired, signed out elsewhere, deactivated or
  // no longer an admin): re-check /me so Gate shows sign-in or AdminLayout redirects,
  // instead of leaving the page on an error that "Try again" can't fix.
  const signedOutRef = useRef(signedOut);
  signedOutRef.current = signedOut;
  const checking = useRef(false);
  useEffect(() => {
    const onExpired = (e: Event) => {
      const detail = (e as CustomEvent<SessionExpiredDetail>).detail;
      // /me failing is already handled above; re-checking it would loop.
      if (detail?.path === "/me" || signedOutRef.current || checking.current) return;
      checking.current = true;
      void reload().finally(() => {
        checking.current = false;
      });
    };
    window.addEventListener(SESSION_EXPIRED_EVENT, onExpired);
    return () => window.removeEventListener(SESSION_EXPIRED_EVENT, onExpired);
  }, [reload]);

  const me = signedOut ? undefined : (data ?? cached);
  return (
    <SessionContext.Provider value={{ me, loading, signedOut, offline: !!cached, error, reload }}>
      {children}
    </SessionContext.Provider>
  );
}

/** Forgets the saved session (used on sign-out). */
export function clearSessionCache(): void {
  writeCache(null);
}

export function useSession(): SessionValue {
  const v = useContext(SessionContext);
  if (!v) throw new Error("useSession outside SessionProvider");
  return v;
}
